import { FC, SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Segment } from '~src/interfaces/segment.interface';

import { SessionUserI } from '~src/interfaces/session.types';
import { StoreI } from '~src/rootReducer';
import { routes } from '~src/routingConfig';
import { okrApi } from '~src/services/ApiController';
import { ApiReduxResp, ThunkAnyAction } from '~src/services/apiMiddleware';
import { ButtonColorTypes, ButtonSizes } from '~src/shared/buttons/basicButtons/BaseButton.sc';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import Dialog from '~src/shared/dialogs/dialog/Dialog';
import { ObjectiveItem, OkrLabel, PostSubobjectiveBody, SubObjectiveItem } from '~src/store/okrs/interfaces';
import { MediaTypes } from '~src/styles/media-templates';
import { visibilityOptions } from '~src/modules/okrs/constants/options';
import {
  CapitalizeFirst,
  PrimarySection,
  SecondarySection,
} from '~src/modules/okrs/manageObjectives/shared/shared.sc';
import { ownerToSegment } from '~src/modules/okrs/manageObjectives/shared/utils';
import { VisibilityObj } from '~src/modules/okrs/manageObjectives/shared/visibility/VisibilitySelector';
import { showNotification } from '~src/store/app/actions';
import { ShowNotification } from '~src/store/app/interfaces';
import { isSelectedWeightingValid } from '~src/modules/okrs/shared/weighting';

import AddKeyResultForm from './keyResults/AddKeyResultForm';
import CreateKeyResults, { KeyResultAdd } from './keyResults/CreateKeyResults';
import {
  ButtonFooter,
  ButtonSpacing,
  ButtonWrapper,
  FormWrapper,
  PageFormContainer,
} from './objectiveAddForm.sc';
import ObjectiveInfo from './ObjectiveInfo';
import ObjectiveLabels from './ObjectiveLabels';
import ObjectiveOwners from './ObjectiveOwners';
import ObjectiveSubInfo from './ObjectiveSubInfo';
import { getLocationState, setLocationState, useSearchParams } from '~src/utils';
import { SortingValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import SubobjectivesList from './subObjectives/SubobjectivesList';

export interface ObjectiveFormData {
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  visibility: VisibilityObj;
  owners: Segment[];
  labels: OkrLabel[];
  status: 'draft' | 'active';
  repeat: string;
  subobjectives: PostSubobjectiveBody[];
  keyResults: Record<string, KeyResultAdd>;
  backToObjectivesList: boolean;
}

interface OwnProps {
  formSubmit: (val: ObjectiveFormData) => ApiReduxResp;
}

interface StateProps {
  loadingCreation: boolean;
  user: SessionUserI | null;
  media?: MediaTypes;
}

interface DispatchProps {
  showNotification: ShowNotification;
}

type Props = OwnProps & StateProps & DispatchProps & RouteComponentProps<any>;

const OkrAddForm: FC<Props> = (props) => {
  const [t] = useTranslation();

  const [showKeyResultModal, setShowKeyResultModal] = useState(false);
  const locationSort = (getLocationState().sort as SortingValues) || null;
  const [, setSort] = useState<string>(locationSort || SortingValues.Created_desc);
  const [searchParams] = useSearchParams();

  // We sometimes want user to go to a specific location after objective is created or cancelled
  const nextRoute = searchParams.next ? String(searchParams.next) : undefined;

  const showKeyResultAddModal = () => {
    setShowKeyResultModal(true);
  };
  const hideKeyResultAddModal = () => {
    setShowKeyResultModal(false);
    setSelectedKeyResult(null);
  };

  const [objectiveCache, setObjectiveCache] = useState<Record<string, SubObjectiveItem>>({});

  function fetchObjective(id: string, assumedWeighting: number | null) {
    void okrApi.get(`objectives/${id}`).then((resp) => {
      const subobjective = resp.data as SubObjectiveItem;
      subobjective.weighting = assumedWeighting;
      setObjectiveCache({ ...objectiveCache, [id]: subobjective });
    });
  }

  function dropObjective(id: string) {
    const newObjectiveCache = { ...objectiveCache };
    delete newObjectiveCache[id];
    setObjectiveCache(newObjectiveCache);
  }

  const [selectedKeyResult, setSelectedKeyResult] = useState<KeyResultAdd | null>(null);

  const selectKeyResult = (keyResult: KeyResultAdd) => {
    setSelectedKeyResult(keyResult);
    showKeyResultAddModal();
  };

  // the solution: don *not* merge data on submit, but change form data to always reflect the data to be submitted.
  // like: if a key result changes, do form.change('key_results', …) !
  const doSubmit = async (val: ObjectiveFormData) => {
    const resp: ThunkAnyAction<ObjectiveItem> = await props.formSubmit(val);
    if (val.backToObjectivesList) {
      goBack();
    } else {
      const id = resp.response.data.id;
      props.history.replace(nextRoute ?? routes.okrs.objectiveDetails(id));
    }
  };

  const goBack = () => {
    props.history.push(nextRoute ?? routes.okrs.list);
  };

  const buttonSize = props.media === 'phone' ? ButtonSizes.Wide : ButtonSizes.Medium;
  const subobjectiveMapper = (body: PostSubobjectiveBody): SubObjectiveItem | undefined => {
    if (objectiveCache[body.id] === undefined) {
      return undefined;
    }
    const o = objectiveCache[body.id];
    o.weighting_locked = !!o.weighting;

    return o;
  };

  const initialValues: Partial<ObjectiveFormData> = useMemo(
    () => ({
      owners: props.user ? [ownerToSegment(props.user)] : [],
      visibility: {
        key: visibilityOptions[0],
      },
      keyResults: {},
      subobjectives: [],
    }),
    [props.user],
  );

  const handleSortKeyResults = useCallback((sort: string) => {
    setSort(sort);
    setLocationState({ sort });
  }, []);

  return (
    <Form initialValues={initialValues} onSubmit={doSubmit}>
      {({ handleSubmit, form, values }) => {
        const submitHook = (
          handleSubmit: any,
          e: SyntheticEvent<any>,
          activate: boolean,
          backToObjectivesList: boolean,
        ) => {
          form.change('status', activate ? 'active' : 'draft');
          form.change('backToObjectivesList', backToObjectivesList);
          handleSubmit(e);
        };
        const saveActivate = (handleSubmit: any) => (e: SyntheticEvent<any>) =>
          submitHook(handleSubmit, e, true, true);
        const saveDraft = (handleSubmit: any) => (e: SyntheticEvent<any>) =>
          submitHook(handleSubmit, e, false, true);

        const addKeyResult = (keyResult: KeyResultAdd) => {
          if (
            !isSelectedWeightingValid(
              values.subobjectives,
              Object.values(values.keyResults).filter((kr) => kr.id !== keyResult.id),
              { id: keyResult.id, weighting: keyResult.weighting },
            )
          ) {
            props.showNotification({
              id: 'weighting_exceeded',
              txtKey: 'x.okr.error.weighting_for_objective_exceeded',
            });
            return;
          }
          if (values.keyResults[keyResult.id]) {
            form.change('keyResults', { ...values.keyResults, [keyResult.id]: keyResult });
          } else {
            form.change('keyResults', { [keyResult.id]: keyResult, ...values.keyResults });
          }
          hideKeyResultAddModal();
        };

        const deleteKeyResult = (keyResultId: string) => {
          const tmpObj = { ...values.keyResults };
          delete tmpObj[keyResultId];
          form.change('keyResults', tmpObj);
        };

        const addSubObjective = (subobjectiveId: string, weighting: number | null) => {
          const postSubobjectiveBody = { id: subobjectiveId, weighting };
          if (
            !isSelectedWeightingValid(
              values.subobjectives,
              Object.values(values.keyResults),
              postSubobjectiveBody,
            )
          ) {
            props.showNotification({
              id: 'weighting_exceeded',
              txtKey: 'x.okr.error.weighting_for_objective_exceeded',
            });
            throw new Error('could not add sub objective');
          }
          fetchObjective(subobjectiveId, weighting);
          const existingSubobjective = values.subobjectives.find((o) => o.id === subobjectiveId);
          if (existingSubobjective) {
            existingSubobjective.weighting = weighting;
            form.change('subobjectives', values.subobjectives.slice());
          } else {
            form.change('subobjectives', values.subobjectives.concat(postSubobjectiveBody));
          }
        };

        const unlinkSubobjective = (subobjectiveToBeDeleted: string) => {
          dropObjective(subobjectiveToBeDeleted);
          form.change(
            'subobjectives',
            values.subobjectives.filter((subObjective) => subObjective.id !== subobjectiveToBeDeleted),
          );
        };

        return (
          <>
            {/*To avoid nested forms we need to put the add key result form outside of the main form*/}
            <Dialog show={showKeyResultModal} onHide={hideKeyResultAddModal}>
              <FormWrapper>
                {showKeyResultModal && (
                  <AddKeyResultForm
                    selectedValues={selectedKeyResult}
                    addKeyResult={addKeyResult}
                    hide={hideKeyResultAddModal}
                  />
                )}
              </FormWrapper>
            </Dialog>
            {/*Main form*/}
            <PageFormContainer onSubmit={handleSubmit}>
              <PrimarySection>
                <ObjectiveInfo />
                <CreateKeyResults
                  showAddModal={showKeyResultAddModal}
                  deleteKeyResult={deleteKeyResult}
                  selectKeyResult={selectKeyResult}
                  keyResults={values.keyResults}
                  onSort={handleSortKeyResults}
                />
                <SubobjectivesList
                  unlinkSubobjective={unlinkSubobjective}
                  subobjectives={values.subobjectives
                    .map(subobjectiveMapper)
                    .filter((sub): sub is SubObjectiveItem => !!sub)}
                  addSubObjective={addSubObjective}
                />
              </PrimarySection>
              <SecondarySection>
                <ObjectiveSubInfo />
                <ObjectiveOwners />
                <ObjectiveLabels />
              </SecondarySection>
              <ButtonFooter>
                <ButtonSpacing />
                <ButtonWrapper>
                  <SecondaryButton
                    type="button"
                    onClick={goBack}
                    data-translation="x.okr.objective.form.abort"
                    $buttonSize={buttonSize}
                  >
                    <CapitalizeFirst>{t('x.okr.objective.form.abort')}</CapitalizeFirst>
                  </SecondaryButton>
                </ButtonWrapper>
                <ButtonWrapper>
                  <PrimaryButtonLoading
                    type="button"
                    onClick={saveDraft(handleSubmit)}
                    $buttonColor={ButtonColorTypes.LightBlue}
                    data-translation="x.okr.objective.form.save-draft"
                    isLoading={props.loadingCreation && values.status === 'draft'}
                    disabled={props.loadingCreation && values.status === 'active'}
                    $buttonSize={buttonSize}
                  >
                    <CapitalizeFirst>{t('x.okr.objective.form.save-draft')}</CapitalizeFirst>
                  </PrimaryButtonLoading>
                </ButtonWrapper>
                <ButtonWrapper>
                  <PrimaryButtonLoading
                    type="button"
                    onClick={saveActivate(handleSubmit)}
                    $buttonColor={ButtonColorTypes.Green}
                    data-translation="x.okr.objective.form.save"
                    isLoading={props.loadingCreation && values.status === 'active'}
                    disabled={props.loadingCreation && values.status === 'draft'}
                    $buttonSize={buttonSize}
                  >
                    <CapitalizeFirst>{t('x.okr.objective.form.save')}</CapitalizeFirst>
                  </PrimaryButtonLoading>
                </ButtonWrapper>
              </ButtonFooter>
            </PageFormContainer>
          </>
        );
      }}
    </Form>
  );
};

const mapStateToProps = (state: StoreI): StateProps => ({
  loadingCreation: state.okrs.objectives.loadingCreation,
  user: state.user.session.user,
  media: state.app.mediaQuery,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  showNotification: (notification) => dispatch(showNotification(notification)),
});

const OkrAddFormConnected = connect(mapStateToProps, mapDispatchToProps)(OkrAddForm);
const OkrAddFormWithRouter = withRouter(OkrAddFormConnected);

export default OkrAddFormWithRouter;
